'use client';
import React, { useMemo, type JSX } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';

interface TextShimmerProps {
  children: string;
  as?: React.ElementType;
  className?: string;
  duration?: number;
  spread?: number;
}

export function TextShimmer({
  children,
  as: Component = 'p',
  className,
  duration = 2,
  spread = 2,
}: TextShimmerProps) {
  const MotionComponent = motion(Component as any);
  const elementRef = React.useRef<any>(null);

  const dynamicSpread = useMemo(() => {
    return children.length * spread;
  }, [children, spread]);

  return (
    <MotionComponent
      className={cn(
        'relative inline-block bg-[length:250%_100%,auto] bg-clip-text text-transparent',
        '[--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),var(--base-gradient-color),#0000_calc(50%+var(--spread)))] [background-repeat:no-repeat,padding-box]',
        className
      )}
      initial={{ backgroundPosition: '150% center' }}
      whileInView={{ backgroundPosition: '-50% center' }}
      viewport={{ once: true, margin: "0px 0px +100px 0px" }}
      transition={{
        duration,
        ease: 'linear',
      }}
      onAnimationComplete={() => {
        // Nettoie le gradient et applique la couleur unie finale
        if (elementRef.current) {
          elementRef.current.style.backgroundImage = 'none';
          elementRef.current.style.backgroundClip = 'initial';
          elementRef.current.style.setProperty('-webkit-background-clip', 'initial');
          elementRef.current.style.color = 'rgba(54,52,52,1)';
          elementRef.current.style.setProperty('-webkit-text-fill-color', 'rgba(54,52,52,1)');
          elementRef.current.className = '';
        }
      }}
      ref={elementRef}
      style={
        {
          '--spread': `${dynamicSpread}px`,
          '--base-color': 'rgba(54,52,52,1)',
          '--base-gradient-color': '#ffffff',
          backgroundImage: `linear-gradient(90deg, rgba(54,52,52,1) calc(50% - ${dynamicSpread}px), #ffffff calc(50% - 10px), #ffffff calc(50% + 10px), rgba(54,52,52,1) calc(50% + ${dynamicSpread}px))`,
          backgroundSize: '300% 100%',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          WebkitTextFillColor: 'transparent',
          backgroundColor: 'transparent',
          textShadow: 'none !important',
          WebkitTextStroke: '0 !important',
          textStroke: '0 !important',
          border: 'none !important',
          outline: 'none !important',
          filter: 'none !important',
        } as React.CSSProperties
      }
    >
      {children}
    </MotionComponent>
  );
}